import { isDrawableLine, routeLinePath } from '../../../components/climbs/route-line';
import type { CragRoute } from '../../../types/crag-route';

/**
 * Screen pixels, as on the 3D view. A fraction of the photo's long edge goes under
 * a pixel in the panel's column, and a thin white line on a sector's ink drawing
 * is gone: the dark edge is what it reads by there, the white on dark rock.
 */
const CORE_WIDTH = 1;
const EDGE_WIDTH = 2.5;
const OPACITY = 0.75;

interface SectorSideLinesProps {
  /** The routes either side of the open one. */
  routes: CragRoute[];
  naturalWidth: number;
  naturalHeight: number;
}

/**
 * The lines either side of the one being drawn, thin and out of the way: enough
 * to place the new line against what is already on the rock.
 *
 * Nothing here takes a press, which on this surface means "put a point here",
 * however close to a neighbour it lands.
 */
export function SectorSideLines({ routes, naturalWidth, naturalHeight }: SectorSideLinesProps) {
  // A line traced on some other picture would land in the wrong place on this one.
  const drawn = routes.filter((route) => isDrawableLine(route.line) && route.line_on_sector_photo);
  if (drawn.length === 0) return null;

  return (
    <svg
      className="pointer-events-none absolute inset-0 size-full"
      viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      data-testid="sector-side-lines"
    >
      {drawn.map((route) => {
        const d = routeLinePath(route.line!.points, naturalWidth, naturalHeight);
        const stroke = { d, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none', vectorEffect: 'non-scaling-stroke' } as const;

        return (
          <g key={route.id} opacity={OPACITY} data-route-id={route.id}>
            <path {...stroke} stroke="rgba(0,0,0,0.7)" strokeWidth={EDGE_WIDTH} />
            <path {...stroke} stroke="white" strokeWidth={CORE_WIDTH} />
          </g>
        );
      })}
    </svg>
  );
}
